import type { GetAuthUsersResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetAuthUsersQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetAuthUsersDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

const mapTrpcUserToGraphQL = (
  user: GetAuthUsersResponseRow
): StrictMappedGraphQLSelection<GetAuthUsersQuery['auth_user'][number]> => ({
  ...user,
  customRoles: (user.customRoles ?? [])
    .filter((customRole) => customRole.customRole != null)
    .map((customRole) => ({ ...customRole, role: customRole.customRole! })),
  userGroupUsers: (user.userGroupUsers ?? [])
    .filter((userGroupUser) => userGroupUser.userGroups != null)
    .map((userGroupUser) => ({
      ...userGroupUser,
      userGroups: userGroupUser.userGroups!,
    })),
});

const mapTrpcUsersToGraphQL = (
  trpcData: GetAuthUsersResponseRow[]
): StrictMappedGraphQLSelection<GetAuthUsersQuery> => ({
  auth_user: trpcData.map(mapTrpcUserToGraphQL),
  auth_user_aggregate: { aggregate: { count: trpcData.length } },
});

type UseGetAuthUsersArgs = Record<string, never>;

/**
 * `frontend.user.users` is gated on `settings_users:read` and a denial reaches
 * this hook as FORBIDDEN, which `useErrorNotification` surfaces as a toast —
 * the V2 behaviour this restores (COI-774).
 *
 * `retry: false` is part of that parity, not a tuning choice. react-query's
 * default re-fetches a failed query three more times, so one settled denial
 * would become four requests and four server-side warns; Apollo issued one.
 * Sentry reporting is deliberately left ON: V2's `graphqlErrorHandler` routed
 * these to Sentry too, so silencing them here would be a new divergence.
 */
export const useGetAuthUsers = createQueryHook<
  UseGetAuthUsersArgs,
  GetAuthUsersResponseRow[],
  GetAuthUsersQuery
>({
  trpcQueryOptions: (trpc) =>
    trpc.frontend.user.users.queryOptions(undefined, { retry: false }),
  mapTrpcDataToGraphQL: mapTrpcUsersToGraphQL,
  graphqlDocument: GetAuthUsersDocument,
  graphqlVariables: () => ({
    where: {
      _or: [
        { RoleKey: { _neq: 'ThirdPartyRespondent' } },
        { RoleKey: { _is_null: true } },
      ],
    },
  }),
});
